import React, { Component, Fragment } from 'react'
import { displayDollars } from '../../helpers/displayFormatHelper'

import AdverseActionsForm from './adverseActionsForm'
import { Loan, AdverseActionReason } from './types'
import { Address } from '../../core/types'

export type Callback<T = void> = (...args: any) => T

export interface Props {
  adverseActionReasons: AdverseActionReason[]
  fetchLoan: Callback
  loan: Loan
  updateLoan: Callback
}

export class UnderwritingLoanApp extends Component<Props> {
  state = { editingAdverseActions: false }

  renderAddresses(label: string, addresses: Address[]) {
    const header1 = addresses.map((addr) => (
      <th key={`h1-${addr.id}`}>ADDRESS</th>
    ))
    const addressCells = addresses.map((addr) => (
      <td key={`addr-${addr.id}`}>
        {addr.line1}
        <br />
        {addr.city}, {addr.state} {addr.zip}
      </td>
    ))
    const header2 = addresses.map((addr) => (
      <th key={`h2-${addr.id}`} className="extra-space">
        START DATE
      </th>
    ))
    const startDates = addresses.map((addr) => (
      <td key={`start-${addr.id}`}>{addr.startDate}</td>
    ))

    return (
      <Fragment>
        <h5 className="section-header">{label} - ADDRESSES</h5>
        <table className="table table loan-table">
          <thead>
            <tr>{header1}</tr>
          </thead>
          <tbody>
            <tr>{addressCells}</tr>
            <tr>{header2}</tr>
            <tr>{startDates}</tr>
          </tbody>
        </table>
      </Fragment>
    )
  }

  renderEmployments(label, employments) {
    const employmentRows = employments.map((employment) => (
      <tr key={employment.id}>
        <td>{employment.employerName}</td>
        <td>{employment.occupation}</td>
        <td>{employment.startDate}</td>
        <td>{employment.endDate}</td>
        <td>{displayDollars(employment.annualGrossIncome)}</td>
        <td>{employment.employerPhoneNumber}</td>
      </tr>
    ))

    return (
      <Fragment>
        <h5 className="section-header">{label} - EMPLOYMENT</h5>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Employer</th>
              <th>Job Title</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Gross Annual Income</th>
              <th>Employer Phone</th>
            </tr>
          </thead>
          <tbody>{employmentRows}</tbody>
        </table>
      </Fragment>
    )
  }

  renderApplicant(applicant, label) {
    const user = applicant.user

    return (
      <Fragment>
        {/* ================================================== */}
        <h5 className="section-header">{label} - PERSONAL</h5>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>First Name</th>
              <th>Last Name</th>
              <th>SSN</th>
              <th>Date of Birth</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{user.firstName}</td>
              <td>{user.lastName}</td>
              <td>{user.ssn}</td>
              <td>{user.birthday}</td>
            </tr>
          </tbody>
        </table>
        {/* ================================================== */}
        <h5 className="section-header">{label} - CONTACT</h5>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Phone</th>
              <th>Email Address</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{user.phone}</td>
              <td style={{ textTransform: 'none' }}>{user.email}</td>
            </tr>
          </tbody>
        </table>
        {/* ================================================== */}
        {this.renderAddresses(label, user.addresses)}
        {/* ================================================== */}
        {this.renderEmployments(label, applicant.employments)}
      </Fragment>
    )
  }

  onSubmit = ({ adverseActions }) => {
    const { loan, updateLoan } = this.props
    updateLoan(loan, { redecision: true, adverseActions })
  }

  renderAdverseActionEditor = () => {
    const { loan, adverseActionReasons } = this.props
    const adverseActions = loan.adverseActions || []

    const initialValues = { adverseActions: [...adverseActions] }

    return (
      <AdverseActionsForm
        onSubmit={this.onSubmit}
        adminStatus={loan.adminStatus}
        adverseActionReasons={adverseActionReasons}
        initialValues={initialValues}
        cancel={this.toggleAdverseActionEditor}
      />
    )
  }

  toggleAdverseActionEditor = () => {
    this.setState({ editingAdverseActions: !this.state.editingAdverseActions })
  }

  renderAdverseActionViewer = () => {
    const { loan, adverseActionReasons } = this.props
    const reasons = adverseActionReasons || {}
    const adverseActions = loan.adverseActions || []
    const labels = adverseActions.map((code) => {
      const reason = reasons[code]
      const label = reason ? reason.description : code
      return <label key={`adverse-action-${code}`}>{label}</label>
    })

    return (
      <div className="actions-section">
        {labels}
        <div className="loan-buttons">
          <button
            className="button button--primary adverse-actions__form__button--submit"
            onClick={this.toggleAdverseActionEditor}
          >
            Set Adverse Actions
          </button>
        </div>
      </div>
    )
  }

  render() {
    const { loan } = (this.props || {}) as Partial<NonNullable<Props>>
    if (!loan) return null
    if (Object.keys(loan).length === 0) return null

    const { applicant, coApplicant, vehicle, subject } = loan
    const { tradein } = subject

    return (
      <div className="content">
        {this.renderApplicant(applicant, 'APPLICANT')}
        {coApplicant && this.renderApplicant(coApplicant, 'CO-APPLICANT')}

        <h5 className="section-header">VEHICLE</h5>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Make</th>
              <th>Model</th>
              <th>Mileage</th>
              <th>VIN</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{vehicle.year}</td>
              <td>{vehicle.make}</td>
              <td>{vehicle.model}</td>
              <td>{vehicle.mileage}</td>
              <td>{vehicle.vin}</td>
            </tr>
          </tbody>
        </table>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Book Value</th>
              <th>Sale Price</th>
              <th>Down Payment</th>
              <th>Trade-In Allowance</th>
              <th>Taxes &amp; Title Fees</th>
              <th>Warranty Amount</th>
              <th>Total Amount Financed</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                {displayDollars(vehicle.latestValuation.privateBestTotal)}
              </td>
              <td>{displayDollars(subject.amount)}</td>
              <td>{displayDollars(subject.downPayment)}</td>
              <td>{tradein && displayDollars(tradein.allowance)}</td>
              <td>
                {displayDollars(
                  parseFloat(subject.lienFee) +
                    parseFloat(subject.tax) +
                    parseFloat(subject.titleFee)
                )}
              </td>
              <td>
                {subject.warrantyFee && displayDollars(subject.warrantyFee)}
              </td>
              <td>{displayDollars(subject.amountFinanced)}</td>
            </tr>
          </tbody>
        </table>

        <h5 className="section-header">LOAN</h5>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Credit Tier</th>
              <th>Annual Percentage Rate</th>
              <th>Term length</th>
              <th>Monthly Payment</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{loan.tier}</td>
              <td>{loan.apr}</td>
              <td>{loan.term}</td>
              <td>{displayDollars(loan.monthlyPayment)}</td>
            </tr>
          </tbody>
        </table>
        <table className="table loan-table">
          <thead>
            <tr>
              <th>Debt to Income</th>
              <th>Payment to Income</th>
              <th>Loan to Value</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{loan.dti}</td>
              <td>{loan.pti}</td>
              <td>{loan.ltvPercent}</td>
            </tr>
          </tbody>
        </table>

        <h5 className="section-header">ADVERSE ACTIONS</h5>
        {this.state.editingAdverseActions
          ? this.renderAdverseActionEditor()
          : this.renderAdverseActionViewer()}
      </div>
    )
  }
}
